import { PORTAL_REQUEST_STATUSES, TASK_TYPES, statusLabel } from "@wms/domain";
import { formatDate, formatDateTime } from "../api/format";

export const CHANGE_WORDS: Record<string, string> = {
  status: "статус",
  task: "задание",
  operation: "операция",
  quantity: "количество",
  from: "откуда",
  to: "куда",
  batch: "партия",
  cargo_unit: "грузовое место",
  lines: "строк",
  number: "номер",
  how: "как",
  place: "место",
  positions: "позиций",
  adjusted_lines: "строк с корректировкой",
  movements: "движений",
  tasks: "заданий",
  comment: "комментарий",
  // R40: cargo unit, serial instance and corrections with a reason
  serial: "экземпляр",
  serials: "экземпляры",
  document: "документ",
  order: "заказ",
  sscc: "SSCC",
  line: "строка",
  contents: "состав",
  weight: "вес",
  reason: "причина",
  packing_list: "упаковочный лист",
  quantity_fact: "факт",
  date: "дата",
  location: "ячейка",
  vehicle: "автомобиль",
  driver: "водитель",
  // QA1101-B01: every key a business action writes into the history has its words (old records included)
  kind: "вид",
  short: "недобор",
  moved: "перемещено",
  uncounted_lines: "не пересчитано строк",
  approved_lines: "утверждено строк",
  cancelled_tasks: "отменены задания",
  changes: "изменения",
  line_no: "строка",
  created: "создано",
  racks: "стеллажей",
  labels: "этикеток",
  format: "формат",
  empty: "место пусто",
  manual: "вручную",
  decision: "решение",
  write_off: "документ списания",
  new_count: "новый пересчёт",
  source_count: "исходный пересчёт",
  nested_count: "вложенных МХ",
  // printed documents and templates
  template: "шаблон",
  template_version: "версия шаблона",
  pages: "страниц",
  pdf_sha256: "контрольная сумма PDF",
  doc_kind: "вид документа",
  fields: "изменены поля",
  version: "версия",
  name: "наименование",
  // services, registers, invoices, payments, acts
  amount: "сумма",
  total_gross: "итого",
  rate: "ставка",
  effective_from: "действует с",
  effective_to: "действует по",
  invoice: "счёт",
  registers: "реестры",
  operations: "операций",
  source: "источник",
  warnings: "предупреждений",
  acknowledged_warnings: "принято предупреждений",
  revision: "редакция",
  corrects: "исправляет",
  new: "новый документ",
  proof: "основание оплаты",
  hash: "контрольная сумма",
  channel: "канал",
  // signing
  side: "сторона",
  provider: "способ подписи",
  package_status: "состояние пакета",
  signer_bin: "БИН/ИИН подписанта",
  expires_at: "действует до",
  email: "почта",
  // the card of a company
  bin: "БИН/ИИН",
  bank_account: "ИИК",
  bank_bic: "БИК",
  bank_name: "банк",
  kbe: "КБе",
  director: "руководитель",
  telegram: "Telegram",
  // users, settings, options, keys
  roles: "роли",
  password: "пароль",
  enabled: "включена",
  value: "значение",
  settings: "настроек",
  feature_options: "опций",
  key: "ключ",
  key_hash: "ключ",
  password_hash: "пароль",
  password_changed_at: "пароль изменён",
  must_change_password: "сменить пароль при первом входе",
  source_type: "источник",
  // lines of a document corrected with a reason
  quantity_plan: "план",
  quantity_min: "план мин.",
  quantity_max: "план макс.",
  quantity_control: "контроль",
  quantity_picked: "отобрано",
  quantity_shipped: "отгружено",
  packages_count: "упаковок",
};

/** Codes a business action stores as values, in words (QA1101-B01): «вид: отбор», never «kind pick». */
const VALUE_WORDS: Record<string, Record<string, string>> = {
  kind: { ...lowerAll(TASK_TYPES), too: "ТОО", ip: "ИП" },
  operation: lowerAll(TASK_TYPES),
  channel: { portal: "кабинет клиента", link: "ссылка", email: "электронная почта" },
  decision: { return_to_stock: "вернуть в оборот", write_off: "списать" },
  doc_kind: { expected_receipt: "ожидаемое поступление", shipment_order: "заказ на отгрузку", stock_count: "инвентаризация" },
  fields: { layout: "макет", name: "наименование", body: "текст" },
  package_status: { partially_signed: "подписан частично", signed: "подписан обеими сторонами", rejected: "отказ" },
  provider: { stub: "тестовый профиль", ncalayer: "NCALayer", qr: "QR-код" },
  side: { operator: "компания-оператор", client: "клиент" },
  source: { operations: "выполненные операции", manual: "вручную" },
  source_type: { manual: "вручную", receipt: "приёмка", shipment: "отгрузка", task: "задание", storage: "хранение" },
  value: { allowed: "разрешено", denied: "запрещено", allow: "разрешить", deny: "запретить", warn: "предупреждать", block: "блокировать", quarantine: "в карантин" },
};

/** A flag in words both ways: «опция: включена / выключена»; the other flags are named only when set. */
const FLAG_WORDS: Record<string, [string, string]> = {
  enabled: ["опция включена", "опция выключена"],
  is_active: ["активен", "не активен"],
};

/** Keys kept for diagnostics only (repeat keys, request ids, inner ids): never part of the line people read. */
const DIAGNOSTIC = new Set(["idempotency_key", "request_id", "request_key", "rendition", "new_count_id", "source_count_id", "package", "dock_id", "source_key"]);

/** Lists of values, never a «from → to» pair even with two items («задания T-000161, T-000162»). */
const LIST_KEYS = new Set(["tasks", "numbers", "registers", "cancelled_tasks", "serials", "fields", "roles", "packages", "created"]);

/** A secret changed: the history keeps the fact, never the value («пароль изменён (значение скрыто)»). */
function isMaskedPair(raw: unknown): boolean {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return false;
  const o = raw as Record<string, unknown>;
  return ("from" in o || "to" in o) && [o.from, o.to].every((v) => v === "***" || v === null || v === undefined) && (o.from === "***" || o.to === "***");
}

function lowerAll(words: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(words).map(([k, v]) => [k, v.toLowerCase()]));
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** «<wms.modules.core.models.Role object at 0x113a34d60>» of an old record: a technical object, not a value. */
const OBJECT_REPR = /^<[\w.]+ object at 0x[0-9a-f]+>$/;
const HEX = /^[0-9a-f]{40,}$/i;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_TIME = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/;

/** A status word in the terms of the card it belongs to: a receipt «in_progress» is «Принимается», a count — «Пересчёт»
 * (ТЗ 1.2 раздел 3.5: the words of the document cards). */
const ENTITY_STATUS: Record<string, Record<string, string>> = {
  expected_receipts: { new: "Черновик", in_progress: "Принимается", accepted: "Принято", confirmed: "Подтверждено", closed: "Закрыто" },
  shipment_orders: { new: "Черновик" },
  stock_counts: { in_progress: "Пересчёт", new: "Новая", approved: "Утверждена", cancelled: "Отменена" },
  cargo_units: { open: "Формируется", closed: "Закрыто", shipped: "Отгружено" },
  serial_numbers: { in_stock: "На складе", shipped: "Отгружен", active: "Зарезервирован", written_off: "Списан" },
  // QA1101-B01: the documents of operations C / D / E, the templates and the requests of the cabinet
  operation_documents: {
    draft: "Черновик",
    ready: "Готово к выполнению",
    in_progress: "Выполняется",
    done: "Перемещено",
    cancelled: "Отменено",
    detected: "Обнаружен",
    quarantine: "В карантине",
    review: "На рассмотрении",
    returned: "Возвращён в оборот",
    to_write_off: "Передан на списание",
    confirmed: "Подтверждено",
    posted: "Списано",
  },
  document_templates: { draft: "Черновик", review: "На проверке", published: "Опубликован", archived: "В архиве" },
  client_requests: PORTAL_REQUEST_STATUSES,
};

/** A number for people («1.250» → «1,25»), but only a real quantity: a code with leading zeros or a long digit
 * string (SSCC, barcode, document number) is shown character for character (BUG-R40-007). */
function numberText(text: string): string {
  if (!/^-?\d+(\.\d+)?$/.test(text)) return text;
  const digits = text.replace(/^-/, "").split(".")[0];
  if ((digits.length > 1 && digits.startsWith("0")) || digits.length > 12) return text;
  return String(Number(text)).replace(".", ",");
}

interface FieldLabel {
  field?: string;
  from?: unknown;
  to?: unknown;
  value?: unknown;
}

/** One readable line of an audit record: «статус: Собран → Упакован · задание T-000117 · количество 8».
 * BUG-R40-002: a field the server labelled (`_labels`) is named by its label and its reference by the name it had at
 * the moment of the change — «Состояние: Доступно → —», never «state_id: bb47ae34-…».
 * QA1101-B01: a key or a value without words is never shown as a code — an empty list says nothing, an inner id stays
 * in the record (the export keeps it), and keys a later release adds are summed up as «другие сведения». */
export function describeChanges(value: unknown, entity?: string): string {
  if (!value || typeof value !== "object") return "";
  const statusWords = (entity && ENTITY_STATUS[entity]) || {};
  const plain = (key: string, v: unknown): string => {
    if (v === null || v === undefined || v === "" || v === "none") return "—";
    if (typeof v === "boolean") return v ? "да" : "нет";
    if (key === "status") return statusWords[String(v)] ?? statusLabel(v);
    if (Array.isArray(v)) {
      if (!v.length) return "—";
      // the warehouses of a login and other lists of inner ids: how many, the ids stay in the record
      if (v.every((x) => typeof x === "string" && UUID.test(x))) return `выбрано ${v.length}`;
      return v.map((x) => plain(key, x)).join(", ");
    }
    if (typeof v === "object") return JSON.stringify(v);
    const text = String(v);
    if (VALUE_WORDS[key]?.[text]) return VALUE_WORDS[key][text];
    if (text === "***") return "скрыто";
    if (UUID.test(text)) return "—";
    if (OBJECT_REPR.test(text)) return "изменено";
    if (HEX.test(text)) return `${text.slice(0, 12)}…`;
    if (ISO_DATE.test(text)) return formatDate(text) || text;
    if (ISO_TIME.test(text)) return formatDateTime(text.replace(" ", "T")) || text;
    return numberText(text);
  };
  const labels = ((value as Record<string, unknown>)._labels ?? {}) as Record<string, FieldLabel>;
  const parts: string[] = [];
  let unknown = 0;
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    if (key.startsWith("_")) continue;
    const label = labels[key];
    const flag = FLAG_WORDS[key];
    if (flag && typeof raw === "boolean" && !label) {
      parts.push(raw ? flag[0] : flag[1]);
      continue;
    }
    if (raw === null || raw === undefined || raw === "" || raw === false) continue;
    if (DIAGNOSTIC.has(key)) continue;
    if (typeof raw === "string" && UUID.test(raw)) continue;
    if (Array.isArray(raw) && raw.length === 0) continue; // «short: []» — nothing was short, nothing to say
    // a column without a label comes back named by itself («key_hash»): then the words of this list name it
    const word = (label?.field && label.field !== key ? label.field : undefined) ?? CHANGE_WORDS[key];
    if (!word) {
      unknown += 1;
      continue;
    }
    if (isMaskedPair(raw)) {
      parts.push(`${word} изменён (значение скрыто)`);
      continue;
    }
    if (label && ("from" in label || "to" in label)) {
      parts.push(`${word}: ${plain(key, label.from)} → ${plain(key, label.to)}`);
      continue;
    }
    if (label && "value" in label) {
      parts.push(`${word}: ${plain(key, label.value)}`);
      continue;
    }
    if (key === "key") {
      parts.push("ключ выдан");
      continue;
    }
    if (Array.isArray(raw)) {
      if (raw.every((x) => typeof x === "string" && UUID.test(x))) {
        parts.push(`${word} ${raw.length}`);
      } else if (key === "operations") {
        parts.push(`${word} ${raw.length}`);
      } else if (key === "short" && raw.every((x) => x && typeof x === "object")) {
        // what planning could not cover: «недобор: строка 1 — 40 (B000003: 50 из 50 отложено)»
        const items = (raw as Record<string, unknown>[]).map((x) =>
          [x.line_no !== undefined ? `строка ${x.line_no}` : null, x.short !== undefined ? `— ${plain("quantity", x.short)}` : null, x.reason ? `(${String(x.reason)})` : null]
            .filter(Boolean)
            .join(" "),
        );
        parts.push(`${word}: ${items.slice(0, 3).join("; ")}${raw.length > 3 ? "; …" : ""}`);
      } else if (raw.every((x) => x && typeof x === "object")) {
        // posted lines of a receipt / shipment: how many and what, not «[object Object]»; rows of a setting (the
        // fields of a barcode structure) — how many
        const items = (raw as Record<string, unknown>[]).map((x) =>
          [
            x.object ?? x.task,
            x.quantity !== undefined ? `× ${plain("quantity", x.quantity)}` : null,
            x.batch ? `партия ${x.batch}` : null,
            x.location ? `→ ${x.location}` : x.from ? `из ${x.from}` : null,
            Array.isArray(x.serials) && x.serials.length ? `экз. ${(x.serials as string[]).join(", ")}` : null,
          ]
            .filter(Boolean)
            .join(" "),
        );
        if (items.every((x) => !x)) parts.push(`${word} ${raw.length}`);
        else parts.push(`${word} ${raw.length}: ${items.slice(0, 3).join("; ")}${raw.length > 3 ? "; …" : ""}`);
      } else if (raw.some((x) => typeof x === "string" && OBJECT_REPR.test(x))) {
        parts.push(`${word}: изменено`);
      } else if (raw.length === 2 && !LIST_KEYS.has(key)) {
        parts.push(`${word}: ${plain(key, raw[0])} → ${plain(key, raw[1])}`);
      } else {
        parts.push(`${word}: ${raw.map((x) => plain(key, x)).join(", ")}`);
      }
    } else if (typeof raw === "object") {
      const o = raw as Record<string, unknown>;
      if ("from" in o || "to" in o) parts.push(`${word}: ${plain(key, o.from)} → ${plain(key, o.to)}`);
      else if ("old" in o || "new" in o) parts.push(`${word}: ${plain(key, o.old)} → ${plain(key, o.new)}`);
      else parts.push(`${word}: ${describeChanges(o, entity) || "изменено"}`);
    } else if (raw === true) {
      parts.push(word);
    } else if (raw === "***") {
      parts.push(`${word} (значение скрыто)`);
    } else {
      parts.push(`${word} ${plain(key, raw)}`);
    }
  }
  if (unknown) parts.push(`другие сведения: ${unknown}`);
  return parts.join(" · ");
}
